import { Box, Stack } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ProviderCard from '@/north-star/care/shortlists/ProviderCard';
import {
  type PublishedShortlist,
  type ShortlistProvider,
} from '@/north-star/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// A published shortlist: the providers in the CAM's chosen order, each with
// its status and the CAM's note. Dismissed providers never reach this view.
const ShortlistView = ({
  shortlist,
  onBack,
  onOpenProvider,
  onToggleSecured,
}: {
  shortlist: PublishedShortlist;
  onBack: () => void;
  onOpenProvider: (provider: ShortlistProvider) => void;
  onToggleSecured: (provider: ShortlistProvider) => void;
}) => (
  <ScreenRoot>
    <StatusBar />

    <Box
      sx={{
        px: 2,
        py: 0.5,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Button
        variant='link'
        ariaLabel='Go back'
        iconLeft='chevron-left'
        onPress={onBack}
      >
        Back
      </Button>
    </Box>

    <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 3 }}>
      {/* Content capped + centred so it stays phone/tablet-shaped when the
          screen is viewed wide (unframed, tablet); at 375px it's full width. */}
      <Stack sx={{ mx: 'auto', maxWidth: 720 }} spacing={2}>
        <PageHeader
          variant='h2'
          title={shortlist.name}
          description={`${shortlist.providers.length} providers · ${shortlist.updated}`}
        />
        {/* Reflowing grid: one column at phone width, two when there's room. */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}
        >
          {shortlist.providers.map((p) => (
            <ProviderCard
              key={p.id}
              provider={p}
              onOpen={onOpenProvider}
              onToggleSecured={onToggleSecured}
            />
          ))}
        </Box>
      </Stack>
    </Box>
  </ScreenRoot>
);

export default ShortlistView;
